
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { getDashboardData } from '@/api/dashboard';
import { formatCurrency } from '@/lib/format-currency';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface RecentPaymentsProps {
  selectedGym: string;
  className?: string;
}

export function RecentPayments({
  selectedGym,
  className,
}: RecentPaymentsProps) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardData', selectedGym],
    queryFn: () => getDashboardData(selectedGym),
    enabled: !!selectedGym && selectedGym !== 'all',
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchIntervalInBackground: true,
  });

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('dashboard.recentPayments')}</CardTitle>
            <CardDescription>
              Here are your most recent payments.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/payments">{t('common.viewAll')}</Link>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted animate-pulse"></div>
                  <div className="space-y-1">
                    <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
                    <div className="h-3 w-16 bg-muted animate-pulse rounded"></div>
                  </div>
                </div>
                <div className="h-4 w-16 bg-muted animate-pulse rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const recentPayments = data?.recentPayments || [];

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{t('dashboard.recentPayments')}</CardTitle>
          <CardDescription>Here are your most recent payments.</CardDescription>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/payments">{t('common.viewAll')}</Link>
        </Button>
      </CardHeader>
      <CardContent>
        {recentPayments.length > 0 ? (
          <div className="divide-y divide-border">
            {recentPayments.map((payment, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <span className="text-xs font-medium text-primary">
                      {payment?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{payment.name}</p>
                    <Badge variant="outline" className="mt-0.5 text-xs">
                      {payment.method}
                    </Badge>
                  </div>
                </div>
                <p className="whitespace-nowrap text-sm font-medium">
                  {formatCurrency(payment.amount, 'ALL')}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t('dashboard.noRecentPayments')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
